import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SseEvent {
  /** raw event payload (string) */
  data: string;
  /** parsed JSON if possible */
  json?: any;
  /** received time */
  at: Date;
}

@Injectable({ providedIn: 'root' })
export class SseService {
  private eventSource?: EventSource;

  private reconnectTimer?: any;
  private reconnectAttempt = 0;
  private readonly reconnectMaxDelayMs = 15000;

  private readonly connected$ = new BehaviorSubject<boolean>(false);
  private readonly events$ = new Subject<SseEvent>();

  // /api/sse/*
  private readonly sseBaseUrl = environment.apiUrl.replace(/\/+$/, '');

  // keep last connected userId to reconnect automatically
  private currentUserId?: number;

  constructor(private zone: NgZone) {}

  isConnected$(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  onEvent$(): Observable<SseEvent> {
    return this.events$.asObservable();
  }

  connect(userId: number): void {
    if (!userId) return;

    // already connected for this user
    if (this.currentUserId === userId && this.eventSource) return;

    this.currentUserId = userId;
    this.disconnect(false);

    const url = `${this.sseBaseUrl}/sse/connect?userId=${encodeURIComponent(String(userId))}`;

    const es = new EventSource(url);

    es.onopen = () => {
      this.zone.run(() => {
        this.connected$.next(true);
        this.reconnectAttempt = 0;
      });
    };

    es.onerror = (err) => {
      this.zone.run(() => {
        this.connected$.next(false);
      });

      try { es.close(); } catch {}
      this.scheduleReconnect();
    };

    const handleAnyEvent = (eventName: string, ev: MessageEvent) => {
      const payload = String((ev as any)?.data ?? '');
      let json: any;
      try { json = payload ? JSON.parse(payload) : undefined; } catch { json = undefined; }

      // If backend uses named event but data is plain, normalize to {type,eventName,data}
      if (json && typeof json === 'object') {
        if (!json.type) json.type = eventName;
      } else if (payload) {
        json = { type: eventName, data: payload };
      }

      this.zone.run(() => {
        this.events$.next({ data: payload, json, at: new Date() });
      });
    };

    // Default message event
    es.onmessage = (ev) => handleAnyEvent('message', ev);

    // Named events (your backend sends: event: new_message)
    es.addEventListener('CONNECTED', (ev) => handleAnyEvent('CONNECTED', ev as MessageEvent));
    es.addEventListener('new_message', (ev) => handleAnyEvent('new_message', ev as MessageEvent));

    // Support both variants: event: DELETE_CHAT and event: delete_chat
    es.addEventListener('DELETE_CHAT', (ev) => handleAnyEvent('DELETE_CHAT', ev as MessageEvent));
    es.addEventListener('delete_chat', (ev) => handleAnyEvent('delete_chat', ev as MessageEvent));

    this.eventSource = es;
  }

  disconnect(clearRetry = true): void {
    try { this.eventSource?.close(); } catch {}
    this.eventSource = undefined;

    this.connected$.next(false);

    if (clearRetry) {
      this.clearReconnect();
      this.currentUserId = undefined;
    }
  }

  /** Call after login when userId known; call again after refresh (it will no-op if same). */
  reconnectIfNeeded(): void {
    if (!this.currentUserId) return;
    this.connect(this.currentUserId);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    const base = 500;
    const delay = Math.min(this.reconnectMaxDelayMs, base * Math.pow(2, this.reconnectAttempt++));

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.currentUserId) this.connect(this.currentUserId);
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.reconnectAttempt = 0;
  }
}
